import {
  CARACTERISTICAS,
  ELENCO_MAX,
  campanhaNaCopa,
  clubePorId,
  clubesDaRodada,
  dataDaRodada,
  divisaoDoHumano,
  ligaDoHumano,
  nomeDaLiga,
  ehRodadaDeCopa,
  ehRodadaDeLibertadores,
  campanhaNaLibertadores,
  campanhaNaSulamericana,
  CONFIANCA_AVISO,
  CONFIANCA_DEMISSAO,
  SALDO_DEMISSAO,
  saldoDemissivel,
  temCopaJunta,
  NOME_FUNCAO,
  funcaoDe,
  nomeCompeticao,
  nomeCompeticaoDaRodada,
  textoRodada,
  notaMedia,
  tabela,
  type Clube,
  type EstadoJogo,
  type Jogador,
} from '../core';
import { textoContrato } from './abas';
import { barraOnline } from './online';
import { app, temporadaAcabou, humano, jogoAtual } from './app';
import { escudo, icone, logoCampeonato } from './icones';
import { blocoHabilidades } from './habilidades';
import { botoesConta } from './conta';
import { NOME_POSICAO, ORDEM_POS, classeIdade, dataBR, dinheiroCurto, dinheiroExtenso, esc, estrelasHtml, ladoTexto, ordinal } from './util';

const MENU: [string, string, string][] = [
  ['agenda', 'agenda', 'Agenda'],
  ['tabelas', 'tabelas', 'Tabelas'],
  ['estadio', 'estadio', 'Estádio'],
  ['juniores', 'juniores', 'Juniores'],
  ['times', 'times', 'Times'],
  ['procura', 'procura', 'Procura'],
  ['treino', 'times', 'Treino'],
  ['tecnicos', 'tecnicos', 'Técnicos'],
  ['historia', 'historia', 'História'],
  ['ranking', 'ranking', 'Ranking'],
  ['opcoes', 'opcoes', 'Opções'],
];

export function topo(e: EstadoJogo): string {
  const data = dataBR(dataDaRodada(e.temporada, Math.min(e.rodada, e.calendario.length)));
  const menu: [string, string, string][] = app.online ? [...MENU.slice(0, -3), ['leilao', 'leilao', 'Leilão'], ...MENU.slice(-3)] : MENU;
  // ofertas de outros técnicos esperando resposta aparecem como número no botão Leilão
  const recebidas = app.online?.visao?.sala.ofertasHumanas.filter((o) => o.vendedorId === e.humanoId).length ?? 0;
  const itens = menu.map(
    ([dlg, ic, nome]) => `<button class="bf-menu-item" data-a="abrir" data-d="${dlg}">${icone(ic)}<span>${dlg === 'leilao' && recebidas > 0 ? `${nome} (${recebidas})` : nome}</span></button>`,
  ).join('');
  return `<header class="bf-topo"><nav class="bf-menu" aria-label="Menu principal">${itens}</nav><span class="bf-data" title="Data do jogo">${data}</span>${botoesConta()}</header>`;
}

function barraConfianca(rotulo: string, pct: number): string {
  const nivel = pct < 35 ? ' baixa' : pct < 65 ? ' media' : '';
  return `<div class="conf"><span>${rotulo}</span><div class="conf-barra${nivel}" role="img" aria-label="${rotulo} ${pct}%"><i style="width:${pct}%"></i><b>${pct}%</b></div></div>`;
}

function proximoJogo(e: EstadoJogo): string {
  if (temporadaAcabou(e)) return '<div class="prox-tit">Temporada encerrada</div><div class="prox-corpo"><span class="cinza">Encerre a temporada no menu Opções.</span></div>';
  const rodada = e.calendario[e.rodada];
  const conf = rodada?.find((c) => c.mandante === e.humanoId || c.visitante === e.humanoId);
  if (!conf) return '<div class="prox-tit">Próximo Jogo</div><div class="prox-corpo">Sem jogo nesta rodada.</div>';
  const casa = conf.mandante === e.humanoId;
  const adv = clubePorId(e, casa ? conf.visitante : conf.mandante);
  const copa = ehRodadaDeCopa(e, e.rodada) || ehRodadaDeLibertadores(e, e.rodada);
  const pos = copa ? 0 : tabela(e).findIndex((l) => l.clubeId === adv.id) + 1;
  return `<div class="prox-tit">Próximo Jogo - ${casa ? 'Casa' : 'Fora'}</div>
    <div class="prox-corpo">${escudo(adv, true)}<div><div>${esc(adv.nome)}${copa ? '' : ` (${ordinal(pos)})`}</div><div class="cinza">${esc(textoRodada(e, e.rodada))}</div></div></div>`;
}

/** Linha da lateral com a situação na competição: posição na liga ou fase na copa. */
function situacaoNaCompeticao(e: EstadoJogo, c: Clube): string {
  if (e.formato !== 'copa') {
    const pos = tabela(e).findIndex((l) => l.clubeId === c.id) + 1;
    const copaJunta =
      (temCopaJunta(e) && !c.liga ? ` · Copa: ${campanhaNaCopa(e, c.id).texto}` : '') +
      (e.libertadores?.grupos.some((g) => g.includes(c.id)) ? ` · Libertadores: ${campanhaNaLibertadores(e, c.id)}` : '') +
      (e.sulamericana && campanhaNaSulamericana(e, c.id) !== 'Não participou' ? ` · Sul-Americana: ${campanhaNaSulamericana(e, c.id)}` : '');
    const ligaDele = ligaDoHumano(e);
    return `${ligaDele ? nomeDaLiga(ligaDele) : e.acesso ? (divisaoDoHumano(e) === 2 ? 'Série B' : 'Série A') : 'Liga'} - ${ordinal(pos)} lugar${copaJunta}`;
  }
  if (temporadaAcabou(e)) return `${nomeCompeticao(e)} - ${campanhaNaCopa(e, c.id).texto}`;
  if (clubesDaRodada(e).includes(c.id)) return esc(textoRodada(e, e.rodada));
  return `${nomeCompeticao(e)} - ${campanhaNaCopa(e, c.id).texto}`;
}

function cartaoJogador(e: EstadoJogo, c: Clube): string {
  const j = c.elenco.find((x) => x.id === app.jogadorSel) ?? c.elenco.find((x) => x.posicao === 'G') ?? c.elenco[0];
  if (!j) return '';
  app.jogadorSel = j.id;
  const nm = notaMedia(j);
  const car = j.caracteristicas.map((i) => CARACTERISTICAS[i]).join('/');
  return `<section class="card-jog" aria-label="Jogador selecionado">
    <div class="card-jog-tit"><span>${esc(j.nome)}${estrelasHtml(j)}</span><b>F:${j.overall}</b></div>
    <p>${NOME_FUNCAO[funcaoDe(j)]} - Lado ${ladoTexto(j.lado)} - ${j.idade} anos</p>
    <p class="cinza">Características: ${car}</p>
    ${blocoHabilidades(j)}
    <p><b>Valor:</b> ${dinheiroExtenso(j.valor)} &nbsp; <b>Salário:</b> ${dinheiroCurto(j.salario)}</p>
    <p class="cinza">Contrato até: ${textoContrato(e, j)}</p>
    <table class="card-stat"><thead><tr><th></th><th>Temporada</th></tr></thead><tbody>
      <tr><th>Jogos:</th><td>${j.jogos}</td></tr><tr><th>Gols:</th><td>${j.gols}</td></tr>
      <tr><th>Assistências:</th><td>${j.assistencias}</td></tr><tr><th>Nota Média:</th><td>${nm === null ? '--' : nm.toFixed(2).replace('.', ',')}</td></tr>
    </tbody></table>
    <div class="card-acoes"><button class="btn peq" data-a="vender" data-id="${j.id}">Vender</button></div>
  </section>`;
}

/**
 * Quando o clube do técnico não jogou a última rodada (eliminado da copa, por exemplo) ou não joga a que está ao vivo, um botão abre
 * os detalhes dos jogos dela. É só um convite: quem não quer ver fica na tela principal e segue mexendo no time.
 */
function botaoVerRodada(e: EstadoJogo): string {
  const ultima = e.ultimaRodada;
  if (!ultima || ultima.partidas.length === 0) return '';
  if (ultima.partidas.some((p) => p.mandante === e.humanoId || p.visitante === e.humanoId)) return '';
  const aoVivo = Boolean(app.online?.visao?.sala.aoVivo);
  return `<button class="btn" data-a="ver-rodada" title="Abre os detalhes dos jogos; para voltar, use o botão da tela ou feche">📺 ${aoVivo ? 'Acompanhar os jogos ao vivo' : 'Ver os jogos da última rodada'}</button>`;
}

/** Online: convites de outros clubes por desempenho (fim da temporada) que o técnico empregado ainda pode aceitar. */
function botaoConvites(): string {
  const n = app.online?.visao?.eu.convites?.length ?? 0;
  if (n === 0 || app.online?.visao?.sala.aoVivo) return '';
  return `<button class="btn principal" data-a="abrir" data-d="convites">📨 Convites de emprego (${n})</button>`;
}

/** Online: com a confiança (a menor entre diretoria e torcida) em 15% ou menos, avisa que em 10% o técnico é demitido. Escondido durante a rodada ao vivo (o resultado ainda não pode vazar). */
function avisoDemissao(c: Clube): string {
  if (!app.online || app.online.visao?.sala.aoVivo) return '';
  const confianca = Math.min(c.confiancaDiretoria, c.confiancaTorcida);
  if (confianca > CONFIANCA_AVISO) return '';
  return `<div class="aviso-demissao" role="alert">⚠️ Confiança em ${confianca}%: se chegar a ${CONFIANCA_DEMISSAO}%, você será demitido.</div>`;
}

/**
 * Jogo solo: no último jogo da temporada (e depois dela), com o caixa em -R$ 10 milhões ou pior, avisa que o técnico será demitido
 * quando a temporada terminar.
 */
function avisoSaldo(e: EstadoJogo, c: Clube): string {
  if (app.online || !saldoDemissivel(e)) return '';
  const ultimoJogo = e.rodada === e.calendario.length - 1;
  const acabou = temporadaAcabou(e);
  if (!ultimoJogo && !acabou) return '';
  const saldo = dinheiroExtenso(c.caixa);
  const limite = dinheiroExtenso(SALDO_DEMISSAO);
  const texto = acabou
    ? `A temporada terminou com o caixa em ${saldo}. Ao encerrá-la você será demitido.`
    : `Último jogo da temporada com o caixa em ${saldo}. Se ela terminar com saldo de ${limite} ou pior, você será demitido.`;
  return `<div class="aviso-demissao" role="alert">⚠️ ${texto}</div>`;
}

export function lateral(e: EstadoJogo): string {
  const c = humano();
  const jogaAgora = !temporadaAcabou(e) && clubesDaRodada(e).includes(c.id);
  return `<aside class="bf-lateral">
    <div class="lat-clube">${escudo(c, true)}<div><div class="lat-nome">${esc(c.nome)}</div><div class="lat-tec">${esc(e.tecnico ?? 'Técnico')}</div></div></div>
    <button class="lat-caixa" data-a="abrir" data-d="financas" title="Finanças do clube">${icone('estadio', 16)}<b>${dinheiroExtenso(c.caixa)}</b></button>
    <div class="lat-liga">${logoCampeonato(e.formato === 'copa' ? 'copa' : divisaoDoHumano(e) === 2 ? 'serie-b' : 'liga', 20)}${situacaoNaCompeticao(e, c)}</div>
    ${barraConfianca('Confiança diretoria:', c.confiancaDiretoria)}
    ${barraConfianca('Confiança torcida:', c.confiancaTorcida)}
    ${avisoDemissao(c)}${avisoSaldo(e, c)}${botaoConvites()}
    <div class="prox">${proximoJogo(e)}</div>
    ${botaoVerRodada(e)}
    <button class="btn grande" data-a="escalar" ${jogaAgora ? '' : 'disabled'}>${icone('bola', 18)} Escalar Time</button>
    ${cartaoJogador(e, c)}
    <div class="lat-rodape"><span title="Jogadores no elenco">${c.elenco.length}/${ELENCO_MAX}</span></div>
  </aside>`;
}

interface Coluna {
  id: string;
  rotulo: string;
  titulo: string;
  valor: (j: Jogador) => number | string;
}

const COLUNAS: Coluna[] = [
  { id: 'pos', rotulo: 'P', titulo: 'Posição', valor: (j) => ORDEM_POS[j.posicao] },
  { id: 'nome', rotulo: 'Nome', titulo: 'Nome', valor: (j) => j.nome },
  { id: 'lado', rotulo: 'L', titulo: 'Lado (E/D)', valor: (j) => j.lado },
  { id: 'forca', rotulo: 'F', titulo: 'Força', valor: (j) => j.overall },
  { id: 'energia', rotulo: 'Energia', titulo: 'Energia', valor: (j) => j.energia },
  { id: 'salario', rotulo: 'Salário', titulo: 'Salário mensal', valor: (j) => j.salario },
  { id: 'passe', rotulo: 'Passe', titulo: 'Valor do passe', valor: (j) => j.valor },
  { id: 'gols', rotulo: 'G', titulo: 'Gols na temporada', valor: (j) => j.gols },
  { id: 'car', rotulo: 'Car.', titulo: 'Características', valor: (j) => j.caracteristicas[0] },
  { id: 'idade', rotulo: 'Idade', titulo: 'Idade', valor: (j) => j.idade },
  { id: 'jogos', rotulo: 'J', titulo: 'Jogos', valor: (j) => j.jogos },
  { id: 'assist', rotulo: 'A', titulo: 'Assistências', valor: (j) => j.assistencias },
  { id: 'nota', rotulo: 'NM', titulo: 'Nota média', valor: (j) => notaMedia(j) ?? -1 },
];

function iconeSituacao(j: Jogador): string {
  if (j.lesaoRodadas > 0) return `<span class="sit lesao" title="Lesionado: ${j.lesaoRodadas} rodada(s)">✚</span>`;
  // os cartões valem por campeonato: o que aparece é o do campeonato da próxima rodada
  const e = app.jogo;
  const comp = e && e.rodada < e.calendario.length ? nomeCompeticaoDaRodada(e, e.rodada) : '';
  const em = comp ? ` (${comp})` : '';
  if (j.suspensaoRodadas > 0) return `<span class="sit vermelho" title="Suspenso: ${j.suspensaoRodadas} jogo(s)${em}"></span>`;
  if (j.amarelosAcumulados > 0) return `<span class="sit amarelo" title="${j.amarelosAcumulados} cartão(ões) amarelo(s)${em}"></span>`;
  return '';
}

export function barraEnergia(pct: number): string {
  const classe = pct < 50 ? ' baixa' : pct < 75 ? ' media' : '';
  return `<div class="en${classe}"><i style="width:${pct}%"></i><b>${pct}%</b></div>`;
}

export function textoCaracteristicas(j: Jogador): string {
  return j.caracteristicas.map((i) => CARACTERISTICAS[i]).join('/');
}

export function elencoOrdenado(c: Clube): Jogador[] {
  const ord = app.ordem;
  const lista = [...c.elenco];
  if (!ord) return lista.sort((a, b) => ORDEM_POS[a.posicao] - ORDEM_POS[b.posicao] || b.overall - a.overall);
  const col = COLUNAS.find((x) => x.id === ord.col);
  if (!col) return lista;
  return lista.sort((a, b) => {
    const va = col.valor(a);
    const vb = col.valor(b);
    const r = typeof va === 'string' ? va.localeCompare(String(vb), 'pt-BR') : (va as number) - (vb as number);
    return (ord.asc ? r : -r) || b.overall - a.overall;
  });
}

export function tabelaElenco(c: Clube): string {
  const cab = COLUNAS.map((col) => {
    const ativo = app.ordem?.col === col.id;
    const seta = ativo ? (app.ordem?.asc ? ' ▲' : ' ▼') : '';
    return `<th class="c-${col.id}"><button class="th-btn" data-a="ordenar" data-col="${col.id}" title="Ordenar por ${esc(col.titulo)}">${col.rotulo}${seta}</button></th>`;
  }).join('');
  const linhas = elencoOrdenado(c)
    .map((j) => {
      const nm = notaMedia(j);
      const sel = j.id === app.jogadorSel ? ' sel' : '';
      return `<tr class="lin${sel}" data-a="sel-jog" data-id="${j.id}" tabindex="0">
        <td class="c-pos"><span class="pos pos-${j.posicao}" title="${NOME_FUNCAO[funcaoDe(j)]}">${funcaoDe(j)}</span></td><td class="c-sit">${iconeSituacao(j)}</td><td class="c-nome esq">${esc(j.nome)}${estrelasHtml(j)}</td>
        <td class="c-lado">${j.lado}</td><td class="c-forca">${j.overall}</td><td class="c-energia">${barraEnergia(j.energia)}</td>
        <td class="c-salario">${dinheiroCurto(j.salario)}</td><td class="c-passe">${dinheiroCurto(j.valor)}</td><td class="c-gols">${j.gols}</td>
        <td class="c-car">${textoCaracteristicas(j)}</td><td class="c-idade ${classeIdade(j.idade)}">${j.idade}</td>
        <td class="c-jogos">${j.jogos}</td><td class="c-assist">${j.assistencias}</td><td class="c-nota ${nm !== null && nm >= 7 ? 'boa' : ''}">${nm === null ? '--' : nm.toFixed(2).replace('.', ',')}</td></tr>`;
    })
    .join('');
  // a coluna do ícone de situação não tem cabeçalho clicável
  const cabCompleto = cab.replace('</th><th class="c-nome">', '</th><th class="c-sit"></th><th class="c-nome">');
  return `<div class="tabela-elenco rolavel"><table class="tab-bf"><thead><tr>${cabCompleto}</tr></thead><tbody>${linhas}</tbody></table></div>`;
}

export function telaPrincipal(): string {
  const e = jogoAtual();
  const c = humano();
  const msg = app.mensagem && !app.dialogo ? `<div class="aviso-bf" role="status">${esc(app.mensagem)}<button class="x" data-a="fechar-msg" aria-label="Fechar aviso">×</button></div>` : '';
  return `${topo(e)}${app.online ? barraOnline() : ''}<div class="bf-corpo">${lateral(e)}<main class="bf-principal">${msg}${tabelaElenco(c)}</main></div>`;
}
